import type { BadgeColor } from '@ksyos/design-system';
import { DateTime } from '@ksyos/date-time';
import {
    Badge,
    Box,
    Button,
    Heading,
    Icon,
    Modal,
    Popover,
    RadioButton,
    RadioGroup,
    Row,
    Spacer,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { GroupMeetingPatientAttendance } from '@common/enums/groupMeetingPatientAttendanceType';
import type { SortDirection } from '@common/enums/sortDirection';
import type {
    IGroupMeetingPatientWithRequestInformation,
    IGroupMeetingWithPatients,
} from '@common/interfaces/groupMeeting';
import type { IReduxState } from '@frontend/configureStore';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { GroupMeetingType, ProcessActivityStatus, Profession } from '@common/enums';
import { GroupMeetingPatientAttendanceType } from '@common/enums/groupMeetingPatientAttendanceType';
import { getDisplayName } from '@common/utils/getDisplayName';
import { EmptyListPlaceholder } from '@frontend/components/elements/EmptyListPlaceholder';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import { FinishedMdoStyledBox as FinishedGroupMeetingStyledBox } from './StyledGroupMeetingComponents';

interface IGroupMeetingPatientsListProps {
    groupMeeting?: IGroupMeetingWithPatients;
    groupMeetingType: GroupMeetingType;
    handleChangeSortDirection?: () => void;
    handleCloseGroupMeeting?: () => void;
    handleSelect: (id: string) => void;
    isClosable?: boolean;
    isSortable?: boolean;
    patients: IGroupMeetingPatientWithRequestInformation[];
    reload?: () => Promise<void>;
    searchBarProps?: ISearchBarProps;
    selectedPatients: string[];
    showRegisterGroupSessionAttendanceButton?: boolean;
    showSelectRowCheckboxes: boolean;
    sortDirection?: SortDirection;
    submitAttendances?: (attendances: { [key: string]: GroupMeetingPatientAttendance }) => Promise<void>;
}

interface ISearchBarProps {
    onSearch: (searchTerm: string) => Promise<void>;
    onSearchTermChange: (searchTerm: string) => void;
    searchTerm: string;
    showSearchBar: boolean;
}

interface IStatusBadgeConfiguration {
    color: BadgeColor;
    translationKey: string;
}

const statusToBadgeMap = new Map<ProcessActivityStatus | 'default', IStatusBadgeConfiguration>([
    [
        'default',
        {
            color: 'neutral',
            translationKey: 'StatusLabelEnum.toAccept',
        },
    ],
    [
        ProcessActivityStatus.draft,
        {
            color: 'orange',
            translationKey: 'StatusLabelEnum.inConcept',
        },
    ],
    [
        ProcessActivityStatus.complete,
        {
            color: 'green',
            translationKey: 'StatusLabelEnum.sent',
        },
    ],
]);

export function GroupMeetingPatientsList(props: IGroupMeetingPatientsListProps) {
    const navigate = useNavigate();
    const [canBeClosed, setCanBeClosed] = useState(false);
    const [canSubmitAttendance, setCanSubmitAttendance] = useState(false);
    const [attendances, setAttendances] = useState<{ [key: string]: GroupMeetingPatientAttendance }>({});
    const [isMissingPatientsModalOpen, setIsMissingPatientsModalOpen] = useState<boolean>(false);
    const profession = useSelector((state: IReduxState) => state.authentication.profession);
    const { patients } = props;

    const searchBarProps = props.searchBarProps;

    useEffect(() => {
        if (props.groupMeetingType === GroupMeetingType.bariMdo) {
            setCanBeClosed(patients.every((patient) => patient.reportStatus === ProcessActivityStatus.complete));
        } else if (props.groupMeetingType === GroupMeetingType.bariGroupSession) {
            setCanBeClosed(
                patients.every((patient) => patient.attendanceType === GroupMeetingPatientAttendanceType.present)
            );
        }
    }, [patients]);

    useEffect(() => {
        if (searchBarProps && !searchBarProps.showSearchBar) {
            searchBarProps.onSearchTermChange('');
        }
    }, [searchBarProps?.showSearchBar]);

    useEffect(() => {
        const patientsWithSavedAttendance = patients.filter(
            (patient) => patient.attendanceType === GroupMeetingPatientAttendanceType.present
        );
        setCanSubmitAttendance(
            patientsWithSavedAttendance.length + Object.keys(attendances).length === patients.length
        );
    }, [attendances, patients]);

    return (
        <Box
            overflow="hidden"
            position="relative"
        >
            {searchBarProps?.showSearchBar && (
                <div style={{ width: '40%' }}>
                    <TextInput
                        onChange={(event) => {
                            searchBarProps.onSearchTermChange(event.target.value);
                        }}
                        onEnterKeyPress={async () => await searchBarProps.onSearch(searchBarProps.searchTerm)}
                        onReset={async () => {
                            searchBarProps.onSearchTermChange('');
                            await searchBarProps.onSearch('');
                        }}
                        placeholder="Zoek patiënt op naam of PID…"
                        size="medium"
                        suffix={{
                            icon: 'search',
                            labelText: 'aria labelled text',
                            onSuffixClick: async () => await searchBarProps.onSearch(searchBarProps.searchTerm),
                        }}
                        value={searchBarProps.searchTerm}
                        variant="outline"
                    />
                </div>
            )}
            {props.showSelectRowCheckboxes && props.isClosable && canBeClosed && (
                <FinishedGroupMeetingStyledBox
                    height="100%"
                    width="100%"
                >
                    <Heading
                        mb={6}
                        size="medium"
                    >
                        {translateKey(
                            props.groupMeetingType === GroupMeetingType.bariMdo
                                ? 'bariMdoGroupMeeting.allMdoReportsSent'
                                : 'bariGroupSessionGroupMeeting.allPatientsPresent'
                        )}
                    </Heading>
                    <Button onClick={props.handleCloseGroupMeeting}>
                        {translateKey(
                            props.groupMeetingType === GroupMeetingType.bariMdo
                                ? 'bariMdoGroupMeeting.closeMdo'
                                : 'bariGroupSessionGroupMeeting.closeGroupSession'
                        )}
                    </Button>
                </FinishedGroupMeetingStyledBox>
            )}
            {patients.length ? (
                <>
                    <TableContainer style={{ marginBottom: 50 }}>
                        <Table spacing="medium">
                            <TableHead>
                                <TableRow>
                                    <TableHeader></TableHeader>
                                    <TableHeader></TableHeader>
                                    <TableHeader>{getSelectedLanguage().patient.patientName}</TableHeader>
                                    <TableHeader>{getSelectedLanguage().common.birthDate}</TableHeader>
                                    <TableHeader>{getSelectedLanguage().common.bsn}</TableHeader>
                                    {props.groupMeetingType === GroupMeetingType.bariMdo && (
                                        <TableHeader>{getSelectedLanguage().service.service}</TableHeader>
                                    )}
                                    <TableHeader
                                        isSortable={props.isSortable}
                                        isSortHeader={props.isSortable}
                                        onClick={() => props.handleChangeSortDirection?.()}
                                        sortDirection={props.sortDirection}
                                    >
                                        {getSelectedLanguage().request.requestDate}
                                    </TableHeader>
                                    {props.groupMeetingType === GroupMeetingType.bariMdo && (
                                        <>
                                            <TableHeader>{getSelectedLanguage().common.remarks}</TableHeader>
                                            <TableHeader></TableHeader>
                                        </>
                                    )}
                                    {props.groupMeetingType === GroupMeetingType.bariGroupSession && (
                                        <TableHeader style={{ textAlign: 'right' }}>
                                            {props.groupMeeting ? 'Aanwezig geweest bij de sessie?' : ''}
                                        </TableHeader>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {patients.map((groupMeetingPatient) => (
                                    <TableRow
                                        key={groupMeetingPatient.id}
                                        onClick={() =>
                                            navigate(`/patients/${groupMeetingPatient.process.patientId}/bariatrics`)
                                        }
                                    >
                                        {props.showSelectRowCheckboxes && shouldDisplayCheckbox(groupMeetingPatient) ? (
                                            <TableCellSelectRow
                                                isSelected={props.selectedPatients.includes(groupMeetingPatient.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                onSelect={() => props.handleSelect(groupMeetingPatient.id)}
                                            />
                                        ) : (
                                            <TableCell width="4%" />
                                        )}
                                        <TableCell width="3%">
                                            <Icon icon="patient" />
                                        </TableCell>
                                        <TableCell
                                            width={props.groupMeetingType === GroupMeetingType.bariMdo ? '15%' : '20%'}
                                        >
                                            {getDisplayName(groupMeetingPatient.process.patient)}
                                        </TableCell>
                                        <TableCell
                                            width={props.groupMeetingType === GroupMeetingType.bariMdo ? '12%' : '18%'}
                                        >
                                            {groupMeetingPatient.process.patient.birthDate
                                                ? DateTime.from(groupMeetingPatient.process.patient.birthDate).format(
                                                      'DD-MM-YYYY'
                                                  )
                                                : ''}
                                        </TableCell>
                                        <TableCell
                                            width={props.groupMeetingType === GroupMeetingType.bariMdo ? '12%' : '18%'}
                                        >
                                            {groupMeetingPatient.process.patient.identifierSocialSecurity}
                                        </TableCell>
                                        {props.groupMeetingType === GroupMeetingType.bariMdo && (
                                            <TableCell
                                                ellipsis
                                                width="12%"
                                            >
                                                {getServiceLabel(groupMeetingPatient.process.service.code, 'workList')}
                                            </TableCell>
                                        )}
                                        <TableCell
                                            width={props.groupMeetingType === GroupMeetingType.bariMdo ? '12%' : '18%'}
                                        >
                                            {groupMeetingPatient.requestDate
                                                ? DateTime.from(groupMeetingPatient.requestDate).format('DD-MM-YYYY')
                                                : ''}
                                        </TableCell>
                                        {props.groupMeetingType === GroupMeetingType.bariMdo ? (
                                            <>
                                                <TableCell
                                                    ellipsis
                                                    width="15%"
                                                >
                                                    {renderRemarks(groupMeetingPatient)}
                                                </TableCell>
                                                <TableCell
                                                    style={{ textAlign: 'right' }}
                                                    width="15%"
                                                >
                                                    {props.isClosable && renderStatusBadge(groupMeetingPatient)}
                                                </TableCell>
                                            </>
                                        ) : (
                                            renderAttendanceBadge(groupMeetingPatient)
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {props.showRegisterGroupSessionAttendanceButton && !canBeClosed && (
                        <Spacer
                            align="end"
                            direction="horizontal"
                            lastChildHasNoSpacing={true}
                            style={{ marginTop: '-2.5rem' }}
                        >
                            <Button
                                onClick={async () =>
                                    canSubmitAttendance
                                        ? await handleSubmitAttendances()
                                        : setIsMissingPatientsModalOpen(true)
                                }
                            >
                                {getSelectedLanguage().bariGroupSessionGroupMeeting.processAttendance}
                            </Button>
                        </Spacer>
                    )}
                    {renderMissingPatientsModal()}
                </>
            ) : (
                <div style={{ paddingBottom: 50 }}>
                    <EmptyListPlaceholder
                        doNotWrapInCard={true}
                        subtitle={translateKey(
                            `${
                                props.groupMeetingType === GroupMeetingType.bariMdo
                                    ? 'bariMdoGroupMeeting'
                                    : 'bariGroupSessionGroupMeeting'
                            }.emptyWaitingList`
                        )}
                    />
                </div>
            )}
        </Box>
    );

    function shouldDisplayCheckbox(groupMeetingPatient: IGroupMeetingPatientWithRequestInformation) {
        if (props.groupMeeting) {
            switch (props.groupMeetingType) {
                case GroupMeetingType.bariMdo:
                    return !(
                        groupMeetingPatient.reportStatus === ProcessActivityStatus.complete ||
                        groupMeetingPatient.reportStatus === ProcessActivityStatus.draft
                    );
                case GroupMeetingType.bariGroupSession:
                    return (
                        profession === Profession.outpatientAssistant &&
                        groupMeetingPatient.attendanceType !== GroupMeetingPatientAttendanceType.present
                    );
                default:
                    return true;
            }
        } else {
            return true;
        }
    }

    function renderRemarks(groupMeetingPatient: IGroupMeetingPatientWithRequestInformation) {
        const renderPopover = (
            labelKey: 'regularMdoRemark' | 'dietitianRemark' | 'psychologistRemark' | 'otherRemark',
            content: string | undefined
        ) =>
            content ? (
                <Row key={labelKey}>
                    <Box
                        as="span"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Popover content={renderRemarkContent(labelKey, content)}>
                            {getSelectedLanguage().bariMdoGroupMeeting[labelKey]}
                        </Popover>
                    </Box>
                </Row>
            ) : null;

        const remarkTypes = ['otherRemark', 'regularMdoRemark', 'dietitianRemark', 'psychologistRemark'] as const;

        return remarkTypes.map((remarkType) => {
            if (remarkType === 'dietitianRemark' || remarkType === 'psychologistRemark') {
                return renderPopover(remarkType, groupMeetingPatient.interimMdoRemark?.[remarkType]);
            } else {
                return renderPopover(remarkType, groupMeetingPatient[remarkType]);
            }
        });
    }

    function renderRemarkContent(
        remarkKey: 'regularMdoRemark' | 'dietitianRemark' | 'psychologistRemark' | 'otherRemark',
        remark: string
    ) {
        return (
            <>
                <b>{getSelectedLanguage().bariMdoGroupMeeting[remarkKey]}: </b>
                {remark}
            </>
        );
    }

    function renderStatusBadge(mdoPatient: IGroupMeetingPatientWithRequestInformation) {
        const badgeConfiguration = statusToBadgeMap.get(mdoPatient.reportStatus ?? 'default');

        return badgeConfiguration ? (
            <Badge
                color={badgeConfiguration.color}
                text={translateKey(badgeConfiguration.translationKey)}
            />
        ) : (
            <Badge
                color="neutral"
                text={getSelectedLanguage().common.unknown}
            />
        );
    }

    function renderAttendanceBadge(groupMeetingPatient: IGroupMeetingPatientWithRequestInformation) {
        let content;
        if (props.groupMeeting?.id) {
            if (groupMeetingPatient.attendanceType === GroupMeetingPatientAttendanceType.present) {
                content = (
                    <Badge
                        color="green"
                        text="Aanwezig"
                    />
                );
            } else if (!groupMeetingPatient.attendanceType && props.showRegisterGroupSessionAttendanceButton) {
                content = (
                    <RadioGroup
                        layout="row"
                        name={groupMeetingPatient.id}
                        onChange={(event) => {
                            setAttendances({
                                ...attendances,
                                [groupMeetingPatient.id]: event.target.value as GroupMeetingPatientAttendance,
                            });
                        }}
                        onClick={(event) => event.stopPropagation()}
                        value={attendances[groupMeetingPatient.id]}
                    >
                        <RadioButton
                            label={getSelectedLanguage().common.yes}
                            value={GroupMeetingPatientAttendanceType.present}
                        />
                        <RadioButton
                            label={getSelectedLanguage().common.no}
                            value={GroupMeetingPatientAttendanceType.noShow}
                        />
                    </RadioGroup>
                );
            }
        }

        if (
            groupMeetingPatient.attendanceType !== GroupMeetingPatientAttendanceType.present &&
            !!groupMeetingPatient.noShowsCount &&
            (groupMeetingPatient.attendanceType || !props.groupMeeting?.id)
        ) {
            content = (
                <Badge
                    color={'orange'}
                    text={`${groupMeetingPatient.noShowsCount} x niet aanwezig geweest`}
                />
            );
        }

        return (
            <TableCell>
                <div style={{ display: 'flex', justifyContent: 'end' }}>{content}</div>
            </TableCell>
        );
    }

    function renderMissingPatientsModal() {
        return (
            <Modal
                isOpen={isMissingPatientsModalOpen}
                onClose={() => setIsMissingPatientsModalOpen(false)}
                size="medium"
            >
                <Modal.Header>Registreer aanwezigheid alle patiënten</Modal.Header>
                <Modal.CloseButton />
                <Modal.Body>
                    <Text>
                        Om de aanwezigheid te verwerken moet van alle patiënten de aanwezigheid of afwezigheid
                        geregistreerd zijn.
                    </Text>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setIsMissingPatientsModalOpen(false)}>
                        {getSelectedLanguage().common.close}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    async function handleSubmitAttendances() {
        if (!props.groupMeeting?.id) {
            return;
        }

        await props.submitAttendances?.(attendances);
        await fetchJson('/api/groupMeeting/removePatients', 'POST', {
            groupMeetingId: props.groupMeeting.id,
            groupMeetingPatientIds: Object.keys(attendances).filter(
                (groupMeetingPatientId) =>
                    attendances[groupMeetingPatientId] === GroupMeetingPatientAttendanceType.noShow
            ),
            type: GroupMeetingType.bariGroupSession,
        });

        setAttendances({});
        await props.reload?.();
    }
}
